import { Sync } from '@mui/icons-material';
import {
  AlertColor,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import strings from 'common/Translation/Translate';
import { FormDataModel } from 'components/Form';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import StyledTabs from 'components/StyledTabs';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ListPage from 'screens/Authenticated/CommonPage';
import MasterDataEntityDrawer from 'screens/Authenticated/CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from 'screens/Authenticated/CommonPage/lastUpdatedColumn';
import { usageTableColumn } from 'screens/Authenticated/CommonPage/usageColumn';
import axiosInstance from 'utils/axios';
import { hotelogix, PageProps, ycs } from 'utils/constants/data';
import { privileges } from 'utils/constants/privileges';
import { URLS } from 'utils/constants/urls';
import { Privilege, languages, selectIntegration } from 'utils/redux/reducer/authentication-slice';
import { pageTitleSx } from 'utils/styles/pageHeader';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import { PaymentMethodForm } from './EditPaymentMethods/PaymentMethodForm';

const MAIN_TAB = 0;
const YCS_TAB = 1;

const PaymentMethodsList: FC = () => {
  const theme = useTheme();
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const userPrivileges = useSelector(Privilege);
  const integration = useSelector(selectIntegration);
  const languageList = useSelector(languages) as { text: string; value: string }[];

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(MAIN_TAB);
  const [ycsRows, setYcsRows] = useState<any[]>([]);
  const [ycsLoading, setYcsLoading] = useState(false);
  const [ycsError, setYcsError] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const isYcs = (integration || '').toUpperCase() === ycs;
  const isHotelogix = integration === hotelogix;
  const syncUrl = isYcs ? URLS.ycsSyncPaymentMethods : URLS.syncPaymentMethods;

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const formModel = useMemo(() => {
    const codeField = { ...PaymentMethodForm(strings)[0], responsive: { xs: 12 } };
    const nameField = PaymentMethodForm(strings)[1];
    const nameFields = (languageList || []).map((language, indexLang) => {
      const field = { ...nameField };
      field.field = `name_${language.value}`;
      field.autoFocus = indexLang === 0;
      field.label = `${field.label} (${language.text})`;
      field.responsive = { xs: 12 };
      return field;
    });
    return [codeField, ...nameFields];
  }, [languageList]);

  const openDrawer = useCallback(
    async (recordId: number | null) => {
      setEditingId(recordId);
      try {
        setDrawerLoading(true);
        const { data } = await axiosInstance.get(URLS.getPaymentMethodsById(recordId as number));
        const container: FormDataModel = { code: data?.code ?? '' };
        (languageList || []).forEach(language => {
          container[`name_${language.value}`] = data?.localizedAttributes?.[language.value]?.name ?? '';
        });
        setFormValues(container);
      } catch (error: any) {
        handleCatch(error);
        return;
      } finally {
        setDrawerLoading(false);
      }
      setDrawerOpen(true);
    },
    [languageList],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSavePaymentMethod = async (body: FormDataModel) => {
    if (!editingId) return;
    try {
      setSaving(true);
      const localizedAttributes = (languageList || []).map(item => ({
        language: item.value,
        name: body[`name_${item.value}`],
      }));
      const payload = { code: body.code, localizedAttributes };
      await axiosInstance.put(URLS.getPaymentMethodsById(editingId), payload);
      setMessage({ display: true, severity: 'success', message: strings.common_update_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = {
    title: strings.menu_text_payment_methods,
    subtitle: strings.paymentMethod_list_subtitle,
    searchField: 'name',
    searchPlaceholder: 'Search payment methods',
    listApi: URLS.getPaginatedPaymentMethods,
    editPrivilege: privileges.edit_payment_methods,
    customEdit: (id: number) => openDrawer(id),
    tableColumn: [
      { field: 'code', headerName: strings.paymentMethodCode_column_name, flex: 2, sortable: true },
      {
        field: 'name',
        headerName: strings.paymentMethodName_column_name,
        flex: 3,
        sortable: true,
        localField: 'localizedAttributes.name',
      },
      usageTableColumn(row => [{ count: row.paymentCount, singular: 'Payment', plural: 'Payments' }]),
      lastUpdatedTableColumn(),
    ],
  };

  const getList = useCallback(
    async (filterData: PageProps) => {
      try {
        setIsLoading(true);
        const requetBody = filterData
          ? filterData
          : { page: index + 1, size: pageSize, filter: filter, sort: ['code:ASC'] };
        const { data } = await axiosInstance.post(pageConfig.listApi, requetBody);
        setPageResponse(data);
        setIsLoading(false);
      } catch (error: any) {
        setIsLoading(false);
      }
    },
    [pageConfig.listApi, index, pageSize, filter],
  );

  const fetchYcsPaymentTypes = useCallback(async () => {
    try {
      setYcsLoading(true);
      setYcsError('');
      const { data } = await axiosInstance.get(URLS.ycsPaymentTypes);
      setYcsRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setYcsRows([]);
      setYcsError(e?.response?.data?.message || e?.message || strings.something_went_wrong);
    } finally {
      setYcsLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await getList(null as any);
    })();
  }, [getList]);

  useEffect(() => {
    if (activeTab === YCS_TAB) {
      fetchYcsPaymentTypes();
    }
  }, [activeTab, fetchYcsPaymentTypes]);

  const tabs = useMemo(() => {
    const base = [{ label: strings.menu_text_payment_methods, value: MAIN_TAB }];
    if (isYcs) {
      base.push({ label: strings.ycs_paymentMethods_tab_label, value: YCS_TAB });
    }
    return base;
  }, [isYcs]);

  useEffect(() => {
    if (!isYcs && activeTab === YCS_TAB) {
      setActiveTab(MAIN_TAB);
    }
  }, [isYcs, activeTab]);

  const syncPaymentMethods = async () => {
    setIsLoading(true);
    try {
      const { status } = await axiosInstance.post(syncUrl);
      if (status === 200 || status === 201) {
        setIsLoading(false);
        await getList(null as any);
      }
    } catch (error) {
      // handle error if needed
    } finally {
      setIsLoading(false);
    }
  };

  const renderYcsTable = () => {
    if (ycsLoading) {
      return <PageLoader />;
    }
    if (ycsError) {
      return (
        <Typography
          sx={{
            p: 2,
            mt: 3,
            borderRadius: 1,
            bgcolor: theme.palette.error.light + '20',
            color: theme.palette.error.main,
            border: `1px solid ${theme.palette.error.light}`,
          }}>
          {ycsError}
        </Typography>
      );
    }
    if (!ycsRows.length) {
      return (
        <Typography
          sx={{
            p: 2,
            mt: 3,
            borderRadius: 1,
            bgcolor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
          }}
          variant="h6">
          {strings.no_data_available}
        </Typography>
      );
    }
    return (
      <Paper
        sx={{
          width: '100%',
          overflow: 'hidden',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          backgroundColor: theme.palette.background.paper,
          mt: 2.5,
        }}>
        <TableContainer sx={{ borderRadius: 2, boxShadow: 'none' }} component={Paper}>
          <Table stickyHeader>
            <TableHead
              sx={{
                '.MuiTableCell-root': {
                  backgroundColor: theme.palette.grey[50],
                  color: theme.palette.text.primary,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  padding: '12px 16px',
                },
              }}>
              <TableRow>
                <TableCell>{strings.ycs_column_ref_id}</TableCell>
                <TableCell>{strings.paymentMethodName_column_name}</TableCell>
                <TableCell>{strings.ycs_column_tax_type}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                '& .MuiTableCell-root': {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                },
              }}>
              {ycsRows.map((row: any, idx: number) => (
                <TableRow key={row?.refId ?? idx}>
                  <TableCell>{row?.refId ?? '-'}</TableCell>
                  <TableCell>{row?.name ?? '-'}</TableCell>
                  <TableCell>{row?.type ?? '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const tabHeader = tabs.length > 1 && (
    <Box sx={{ mb: 2 }}>
      <StyledTabs
        tabs={tabs}
        value={activeTab}
        onChange={(_: any, value: number) => setActiveTab(value)}
      />
    </Box>
  );

  if (activeTab === YCS_TAB && isYcs) {
    return (
      <>
        <Typography sx={{ ...pageTitleSx, mb: 1 }}>{strings.menu_text_payment_methods}</Typography>
        {tabHeader}
        {renderYcsTable()}
        {message.display && (
          <Notification
            isOpen={message.display}
            message={message.message}
            severity={message.severity as AlertColor}
            closeNotification={closeNotification}
          />
        )}
      </>
    );
  }

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      {tabHeader}
      <div style={{ textAlign: 'right' }}>
        {userPrivileges?.includes(privileges.view_payment_methods) && (isHotelogix || isYcs) && (
          <Button disabled={isLoading} variant="text" onClick={syncPaymentMethods}>
            <Sync />
          </Button>
        )}
      </div>
      <ListPage pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} isLoading={isLoading} />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={strings.paymentMethod_title_edit}
        isEdit
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        onClose={closeDrawer}
        onSave={handleSavePaymentMethod}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default PaymentMethodsList;
